/**
 * @prism-graphics/bridge/editor: the editor's side (inside PRISM's iframe). See docs/bridge-api.md.
 *
 * A message is accepted only when its source is the parent window, its origin is exactly one of `hostOrigins`,
 * and it validates (protocol, supported version, host type, payload). After the first host:init, the host's origin
 * is fixed: later messages must come from it, and every reply goes only to it (never '*'). Before that,
 * editor:ready goes to each allowed origin as its own target, so only the real parent can receive it (it carries
 * nothing but the protocol versions). editor:ready is repeated until host:init arrives, in case the host page
 * attaches its listener after the iframe has loaded.
 */
import {
  type BridgeLogger,
  type EditorMessageType,
  type ErrorPayload,
  type HostInit,
  type HostMessage,
  type Payloads,
  type SavedPayload,
  type Theme,
  type Token,
  SUPPORTED_VERSIONS,
  envelope,
  isEnvelope,
  loggableType,
  validateMessage,
} from './protocol.ts';

export * from './protocol.ts';

export interface EditorOptions {
  /** Exact origins of PRISM (scheme://host[:port]); no wildcards. */
  hostOrigins: readonly string[];
  /** Protocol versions the editor speaks (default [1]). */
  versions?: readonly number[];
  logger?: BridgeLogger;
  /** How often editor:ready is repeated until host:init arrives (ms; default 1000, at most 60 times). */
  readyIntervalMs?: number;
  /** For tests: the window to use (default globalThis). */
  window?: Window;
}

export interface EditorBridge {
  /** Whether the editor runs in a frame (a parent window exists). */
  readonly embedded: boolean;
  /** The host's origin, once host:init has arrived. */
  readonly hostOrigin: string | undefined;
  /** The first host:init (later ones are delivered as token and theme updates). */
  onInit(handler: (init: HostInit) => void): () => void;
  onToken(handler: (token: Token) => void): () => void;
  onTheme(handler: (theme: Theme) => void): () => void;
  /** The handler answers with saved() or error() and the same requestId. If it throws, SAVE_FAILED is sent. */
  onRequestSave(handler: (requestId: string) => void | Promise<void>): () => void;
  /** The handler answers with close() or closeCancelled() (or error()) and the same requestId. */
  onRequestClose(handler: (requestId: string) => void | Promise<void>): () => void;
  dirty(dirty: boolean): void;
  saved(result: SavedPayload, requestId?: string): void;
  close(result: { saved: boolean }, requestId?: string): void;
  closeCancelled(requestId: string): void;
  error(error: ErrorPayload, requestId?: string): void;
  /** Stop listening and stop repeating editor:ready. */
  disconnect(): void;
}

const LOG_PREFIX = '[prism-graphics bridge/editor]';
const MAX_READY_REPEATS = 60;

function normaliseOrigin(o: string): string {
  const u = new URL(o);
  if (u.origin === 'null' || u.origin !== o.replace(/\/$/, '')) throw new Error(`connectEditor: "${o}" is not an exact origin (scheme://host[:port])`);
  return u.origin;
}

export function connectEditor(options: EditorOptions): EditorBridge {
  const win: Window = options.window ?? (globalThis as unknown as Window);
  const origins = new Set(options.hostOrigins.map(normaliseOrigin));
  const versions = [...(options.versions ?? [1])].filter((v) => SUPPORTED_VERSIONS.includes(v));
  if (!versions.length) throw new Error('connectEditor: none of the requested protocol versions is supported');
  const logger = options.logger;
  const parent = win.parent;
  const embedded = !!parent && parent !== win;

  let hostOrigin: string | undefined;
  let initReceived = false;
  let firstInit: HostInit | undefined;
  let disconnected = false;
  let readyTimer: ReturnType<typeof setInterval> | undefined;
  let lastDirty: boolean | undefined;

  const handlers = {
    init: new Set<(init: HostInit) => void>(),
    token: new Set<(t: Token) => void>(),
    theme: new Set<(t: Theme) => void>(),
    save: new Set<(id: string) => void | Promise<void>>(),
    close: new Set<(id: string) => void | Promise<void>>(),
  };

  const log = (level: keyof BridgeLogger, message: string, details?: Record<string, unknown>) => {
    try {
      logger?.[level]?.(`${LOG_PREFIX} ${message}`, details);
    } catch {
      // a logger never breaks the bridge
    }
  };

  const call = <A>(set: Set<(a: A) => unknown>, arg: A, name: string) => {
    for (const h of set) {
      try {
        const r = h(arg);
        if (r && typeof (r as Promise<unknown>).catch === 'function') (r as Promise<unknown>).catch(() => log('error', `the ${name} handler failed`));
      } catch {
        log('error', `the ${name} handler threw`);
      }
    }
  };

  const post = <T extends EditorMessageType>(type: T, payload: Payloads[T], requestId?: string) => {
    if (disconnected || !embedded) return;
    if (!hostOrigin) {
      log('warn', `cannot send ${type} before host:init`);
      return;
    }
    parent.postMessage(envelope(type, payload, requestId), hostOrigin);
    log('debug', `sent ${type}`);
  };

  const postReady = () => {
    if (disconnected || !embedded || initReceived) return;
    const msg = envelope('editor:ready', { versions: [...versions] });
    // Prefer the parent's origin where the browser tells it; otherwise each allowed origin (a mismatched target
    // origin is dropped by the browser, so only the real parent receives it).
    const ancestors = (win.location as Location & { ancestorOrigins?: DOMStringList }).ancestorOrigins;
    const known = ancestors && ancestors.length > 0 ? ancestors[0] : undefined;
    const targets = known && origins.has(known) ? [known] : [...origins];
    for (const t of targets) {
      try {
        parent.postMessage(msg, t);
      } catch {
        // a malformed target origin cannot happen (normalised); ignore
      }
    }
  };

  const handle = (m: HostMessage, origin: string) => {
    switch (m.type) {
      case 'host:init': {
        hostOrigin = origin;
        if (readyTimer !== undefined) clearInterval(readyTimer);
        readyTimer = undefined;
        if (!initReceived) {
          initReceived = true;
          firstInit = m.payload;
          call(handlers.init, m.payload, 'init');
          if (lastDirty) post('editor:dirty', { dirty: true });
        } else {
          // A repeated init (the host re-initialises): a new token and theme for this session.
          call(handlers.token, m.payload.token, 'token');
          call(handlers.theme, m.payload.theme, 'theme');
        }
        return;
      }
      case 'host:token':
        call(handlers.token, { value: m.payload.value, expiresAt: m.payload.expiresAt }, 'token');
        return;
      case 'host:theme':
        call(handlers.theme, m.payload.theme, 'theme');
        return;
      case 'host:request-save': {
        const id = m.requestId!;
        if (!handlers.save.size) {
          post('editor:error', { code: 'NOT_READY', message: 'The editor cannot save yet' }, id);
          return;
        }
        for (const h of handlers.save) {
          Promise.resolve()
            .then(() => h(id))
            .catch(() => {
              log('error', 'the request-save handler failed');
              post('editor:error', { code: 'SAVE_FAILED', message: 'The graphic could not be saved' }, id);
            });
        }
        return;
      }
      case 'host:request-close': {
        const id = m.requestId!;
        if (!handlers.close.size) {
          post('editor:close', { saved: false }, id);
          return;
        }
        for (const h of handlers.close) {
          Promise.resolve()
            .then(() => h(id))
            .catch(() => {
              log('error', 'the request-close handler failed');
              post('editor:error', { code: 'SAVE_FAILED', message: 'The editor could not answer the close request' }, id);
            });
        }
        return;
      }
    }
  };

  const listener = (event: MessageEvent) => {
    if (disconnected || !isEnvelope(event.data)) return;
    const type = loggableType(event.data);
    if (!embedded || event.source !== parent) {
      log('warn', 'ignored message: not from the parent window', { type, origin: event.origin });
      return;
    }
    if (!origins.has(event.origin) || (hostOrigin !== undefined && event.origin !== hostOrigin)) {
      log('warn', 'ignored message: origin not allowed', { type, origin: event.origin });
      return;
    }
    const v = validateMessage(event.data, 'host');
    if (!v.ok) {
      log('warn', `ignored message: ${v.reason}`, { type });
      return;
    }
    if (v.message.type !== 'host:init' && !initReceived) {
      log('warn', `ignored ${v.message.type}: no host:init yet`);
      return;
    }
    log('debug', `received ${v.message.type}`);
    handle(v.message, event.origin);
  };

  win.addEventListener('message', listener);
  if (embedded) {
    // After the caller has registered its handlers (same task), announce; repeat until host:init arrives.
    queueMicrotask(postReady);
    let repeats = 0;
    readyTimer = setInterval(() => {
      if (initReceived || ++repeats > MAX_READY_REPEATS) {
        if (readyTimer !== undefined) clearInterval(readyTimer);
        readyTimer = undefined;
        return;
      }
      postReady();
    }, options.readyIntervalMs ?? 1000);
  }

  const add = <T>(set: Set<T>, h: T) => {
    set.add(h);
    return () => void set.delete(h);
  };

  return {
    embedded,
    get hostOrigin() {
      return hostOrigin;
    },
    onInit(h) {
      const off = add(handlers.init, h);
      // An init that arrived before this handler was registered is delivered now.
      if (firstInit) {
        const init = firstInit;
        queueMicrotask(() => handlers.init.has(h) && h(init));
      }
      return off;
    },
    onToken: (h) => add(handlers.token, h),
    onTheme: (h) => add(handlers.theme, h),
    onRequestSave: (h) => add(handlers.save, h),
    onRequestClose: (h) => add(handlers.close, h),
    dirty(d) {
      if (d === lastDirty) return;
      lastDirty = d;
      // Before host:init it is sent with the init (below).
      if (hostOrigin) post('editor:dirty', { dirty: d });
    },
    saved(result, requestId) {
      post('editor:saved', { assetId: result.assetId, version: result.version, name: result.name }, requestId);
    },
    close(result, requestId) {
      post('editor:close', { saved: result.saved }, requestId);
    },
    closeCancelled(requestId) {
      post('editor:close-cancelled', {}, requestId);
    },
    error(error, requestId) {
      post('editor:error', { code: error.code, message: error.message }, requestId);
    },
    disconnect() {
      disconnected = true;
      if (readyTimer !== undefined) clearInterval(readyTimer);
      readyTimer = undefined;
      win.removeEventListener('message', listener);
    },
  };
}
